// @flow
import * as React from 'react';
import { types as t } from '../validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from '../validation/runtime-checks/index.js';
import Figure from '../Figure.js';
import { usePropsValidation } from '../validation/runtime-checks/index.js';

const ORIGIN = 'https://jsx-images.mathspace.co';

// The Tennessee RFP requires us to give students "technology"
// to use when doing their mathematics. In practice this means we are
// giving them a bunch of half-assed "calculator" tools they can use while
// working on questions. We need screenshots of the tools in action to
// guide the students in how to use them. This requires bitmap images.
// In general, it is very important that content authors can only use svgs
// in content. Therefore we do NOT want to allow normal Image components
// to support bitmaps. As a result, we have this very specialized component
// that is the only place that bitmaps can get into content. The hope is
// this will be locked down to enough to prevent abuse/contagion of bitmap
// images throughout the content.
export function SolidifyTechnologyToolScreenshot(props: {|
  // The path to the image in the bucket. This is relative to
  // the technology-tool-screenshots "folder".
  path: string,
  // The description that screenreaders will use to describe the image to a user
  accessibilityDescription: string,
  // Dimensions are mandatory so that the layout can be computed
  // by the browser before any image data has been received.
  width: number,
  height: number,
  ...CompilerInjectedProps,
|}) {
  const { path, accessibilityDescription, width, height } = props;
  const imageUrl = ORIGIN + '/technology-tool-screenshots' + path;

  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    SolidifyTechnologyToolScreenshot,
  );

  React.useEffect(() => {
    if (failedValidation || !props._performValidation) return;

    // Sync validation
    validateSpecifiedScreenshotDimensions(width, height, locations, addErrors);

    // Async validation
    let isStaleEffect = false;
    validateIsPng(imageUrl, locations, addErrors, isStaleEffect);
    validateNaturalScreenshotDimensions(
      imageUrl,
      locations,
      addErrors,
      isStaleEffect,
    );

    return () => {
      isStaleEffect = true;
    };
  });

  return (
    <Figure>
      <img
        src={imageUrl}
        alt={accessibilityDescription}
        width={width}
        height={height}
        // We make it block so that ancestor elements don't end up with
        // whitespace at the bottom due to lineHeight computations triggered
        // by inline descendents.
        style={{ display: 'block', margin: '0 auto' }}
      />
    </Figure>
  );
}
SolidifyTechnologyToolScreenshot.spec = {
  path: pathChecker,
  accessibilityDescription: t.string,
  width: t.number,
  height: t.number,
};

// Enforces that paths are strings which provide a versionId /blah?versionId=1234
function pathChecker(props, propName) {
  const value = props[propName];

  const failure = t.string(props, propName);
  if (failure !== null) return failure;
  // Refinement for Flow as it doesn't know t.string enforces a string type
  if (typeof value !== 'string') {
    throw Error('runtime typecheck invariant failure');
  }

  const url = new URL(`http://fake${value}`);
  const versionId = url.searchParams.get('versionId');
  if (versionId === null) {
    return {
      typeDescription: pathChecker.description,
      reason: 'must specify a versionId.',
    };
  }

  if (!/solidify-(lesson|teacher-note)-\d+.png/.test(value)) {
    return {
      typeDescription: pathChecker.description,
      reason:
        'Solidify documents can only contain <SolidifyTechnologyToolScreenshot>s that source images identified with `solidify-lesson` or `solidify-teacher-note` ',
    };
  }

  return null;
}
pathChecker.description = 'BucketPath';

function validateSpecifiedScreenshotDimensions(
  width,
  height,
  locations,
  addErrors,
) {
  const allowedRenderDimensions = [
    { width: 596, height: 160 },
    { width: 596, height: 200 },
    { width: 596, height: 260 },
    { width: 596, height: 360 },
    { width: 596, height: 460 },
    { width: 596, height: 560 },
  ];

  if (
    allowedRenderDimensions.some(
      (d) => d.width === width && d.height === height,
    ) === false
  ) {
    addErrors([
      {
        message: `${width}×${height} is invalid. Must be one of: \n${allowedRenderDimensions
          .map(({ width, height }) => `${width}×${height}`)
          .join('\n')}`,
        ...locations.SolidifyTechnologyToolScreenshot,
      },
    ]);
  }
}

function validateNaturalScreenshotDimensions(
  imageUrl,
  locations,
  addErrors,
  isStaleEffect,
) {
  // $FlowIssue Image constructor doesn't have the correct signature
  const image = new Image(imageUrl);
  image.addEventListener('load', () => {
    if (isStaleEffect) return;
    const { naturalWidth, naturalHeight } = image;

    // TODO share these with BucketBrowser upload validation somehow
    const allowedNaturalDimensions = [
      { width: 1490, height: 400 },
      { width: 1490, height: 500 },
      { width: 1490, height: 650 },
      { width: 1490, height: 900 },
      { width: 1490, height: 1150 },
      { width: 1490, height: 1400 },
    ];

    if (
      allowedNaturalDimensions.some(
        (d) => d.width === naturalWidth && d.height === naturalHeight,
      ) === false
    ) {
      addErrors([
        {
          message: `The uploaded png has invalid dimensions: ${naturalWidth}×${naturalHeight}. Must be one of: \n${allowedNaturalDimensions
            .map(({ width, height }) => `${width}×${height}`)
            .join('\n')}`,
          ...locations.SolidifyTechnologyToolScreenshot,
        },
      ]);
    }
  });
  image.src = imageUrl;
}

async function validateIsPng(imageUrl, locations, addErrors, isStaleEffect) {
  const res = await fetch(imageUrl);
  const blob = await res.blob();
  if (isStaleEffect) return;
  if (blob.type !== 'image/png') {
    addErrors([
      {
        message: `You must use a .png for screenshots. The image type is ${blob.type}`,
        ...locations.SolidifyTechnologyToolScreenshot,
      },
    ]);
  }
}
